import {
  ActivitiesBySponsorQuery,
  ActivitiesBySponsorQueryVariables,
  ActivitiesByUsersActivity,
  Activity,
  AssessmentRole,
  AvailableActivitiesArgs,
  AvailableActivitiesQuery,
  ListActivitiesQuery,
  ModelActivityConnection,
  PaginatableActivitiesByUsersResult
} from "../../API";
import { API, GraphQLQuery } from "@aws-amplify/api";
import * as queries from "../../graphql/queries";
import { listQueryAutoPaginated } from "../api/apiUtils";

export const fetchAllActivities = async () => {
  return await listQueryAutoPaginated<
    ListActivitiesQuery,
    Activity,
    ModelActivityConnection
  >(queries.listActivities, "listActivities", "items");
};

export const fetchActivitiesBySponsor = async (sponsorId: string) => {
  const predicate: ActivitiesBySponsorQueryVariables = {
    sponsorId: sponsorId
  };
  return await listQueryAutoPaginated<
    ActivitiesBySponsorQuery,
    Activity,
    ModelActivityConnection
  >(queries.activitiesBySponsor, "activitiesBySponsor", "items", predicate);
};

export const fetchAvailableActivities = async (
  input: AvailableActivitiesArgs
) => {
  input.limit = 2500;
  input.page = 1;

  let toReturn: ActivitiesByUsersActivity[] = [];

  const getNextPage = async (): Promise<ActivitiesByUsersActivity[]> => {
    const results = (await API.graphql<GraphQLQuery<AvailableActivitiesQuery>>({
      query: queries.availableActivities,
      variables: { input },
      authMode: "AMAZON_COGNITO_USER_POOLS"
    })) as { data: AvailableActivitiesQuery };

    const { data } = results;
    const dataChildren = data[
      "availableActivities"
    ] as PaginatableActivitiesByUsersResult;
    const items = dataChildren["activities"] as ActivitiesByUsersActivity[];
    const nextPage =
      dataChildren["nextPage" as keyof PaginatableActivitiesByUsersResult];

    toReturn = [...toReturn, ...items];

    if (nextPage && typeof nextPage === "number") {
      input.page = nextPage;
      return await getNextPage();
    } else {
      return toReturn;
    }
  };

  const finalResults = await getNextPage();
  return finalResults;
};

export type AvailableActivitiesInput = {
  subjectUserId: string;
  // SE-1787: scopes the list to networks this rater may rate the subject on.
  raterUserId?: string;
  sponsorId?: string;
  categoryId?: string;
  sortOrder?: "asc" | "desc";
  query?: string;
};
export type AvailableActivity = {
  activityId: string;
  activityTitle: string;
  sponsorId: string;
  sponsorName: string;
  sponsorDescription?: string;
  sponsorLogoUrl?: string;
};
export const availableActivitiesAPI = async (
  input: AvailableActivitiesInput
) => {
  const availableActivities = (await API.get(
    "simpl-api",
    "available-activities",
    {
      queryStringParameters: {
        ...input
      }
    }
  )) as AvailableActivity[];
  return availableActivities;
};

export type AvailableActivityType = {
  sponsorId: string;
  activityLabel: string;
  sponsorLogoUrl?: string;
};
export type AvailableActivityTypesInput = {
  subjectUserId: string;
  // SE-1787: scopes the list to networks this rater may rate the subject on.
  raterUserId?: string;
};
export const availableActivityTypesAPI = async (
  input: AvailableActivityTypesInput
) => {
  const availableActivityTypes = (await API.get(
    "simpl-api",
    "available-activity-types",
    {
      queryStringParameters: {
        ...input
      }
    }
  )) as AvailableActivityType[];
  return availableActivityTypes;
};

export type AvailableCategory = {
  categoryId: string;
  categoryName: string;
};
export type AvailableCategoriesInput = {
  subjectUserId: string;
  sponsorId?: string;
  // SE-1787: scopes the list to networks this rater may rate the subject on.
  raterUserId?: string;
};
export const availableCategoriesAPI = async (
  input: AvailableCategoriesInput
) => {
  const availableCategories = (await API.get(
    "simpl-api",
    "available-categories",
    {
      queryStringParameters: {
        ...input
      }
    }
  )) as AvailableCategory[];
  return availableCategories;
};

export type AssessmentSummaryProgram = {
  id: string;
  name: string;
  institutionName: string;
  creationBehavior: "CREATE ASSESSMENT" | "CREATE & START ASSESSMENT";
  notes: {
    title: string;
    prompt: string;
    helpText: string;
  };
};
export type AssessmentSummary = {
  programs: AssessmentSummaryProgram[];
  shortestCreationDurationHours: number;
  branding: {
    sponsorId: string;
    sponsorName: string;
    sponsorLogoUrl: string;
  };
};
export type AssessmentSummaryInput = {
  subjectUserId: string;
  raterUserId: string;
  selectedUserId: string;
  currentUsersAssessmentRole: AssessmentRole;
  activityIds: string[];
};
export const assessmentSummaryAPI = async (input: AssessmentSummaryInput) => {
  const assessmentSummary = (await API.get("simpl-api", "assessment-summary", {
    queryStringParameters: {
      ...input
    }
  })) as AssessmentSummary;
  return assessmentSummary;
};

// Initiate Assessment Request
export type AssessmentRequestReturn = {
  assessmentEventsToComplete: string[];
};
export type RequestAssessmentInput = {
  loggedInUserId: string;
  participantUserIds: string[];
  activityIds: string[];
  programId: string;
  activityEventDate: string;
  notes?: string;
  subjectUserId: string;
  raterUserId: string;
};
export const requestAssessmentAPI = async (input: RequestAssessmentInput) => {
  const requestAssessment = (await API.post("simpl-api", "assessment/request", {
    body: {
      ...input
    }
  })) as AssessmentRequestReturn;
  return requestAssessment;
};
